import type { ApiResponse, ITaskJSONFormQueryData, CaseHistoryOat } from '@/types/api'
import type {
  ApiBoolResult,
  ApiIdResult,
  ApiValue,
  CaseHistoryPortalConfig,
  CaseHistoryTaskAction,
  FormTaskAction,
  FvTaskAction,
  StatementTaskAction
} from '@/types/task-actions'
import { getClient } from '@/core/services/ApiService'
import { parseApiBoolResult } from '@/utils/parseApiBoolResult'

const secured = () => getClient()

interface IPatient {
  id: number
  acctNo: string
  lastName: string
  firstName: string
  middleName: string
  mi: string
  suffix: string
  title: string
  name: string
  nameWithNN: string
  identifier: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  homePhone: string
  workPhone: string
  cellPhone: string
  email: string
  dob: string
  sex: string
  ssn: string
  mbi: string
  active: boolean
  refSourceID: number
  refSource: string
  refSourceGroup: string
  refSourceActive: boolean
  referringPatID: number
  referringPat: string
  patientStatusID: number
  employStatID: number
  employStat: string
  maritalStatID: number
  maritalStat: string
  emerContact: string
  emerRelation: string
  emerPhone: string
  height_inches: number
  weight_lbs: number
  languageID: number
  language: string
  firstVisitDate: string
  lastVisitDate: string
  lastVisit: string
  nickName: string
  workExt: string
  collections: boolean
  collectionsDate: string
  pin: string
  sendStmt: boolean
  stmtMsg: string
  emailRefused: boolean
  emailRefusedDate: string
  emailRefusedUser: string
  inactiveDate: string
  memo: string
  attorney: string
  familyDr: string
  medicare: boolean
  medicaid: boolean
  entryDate: string
  defCaseID: number
  insCount: number
  currentPatBal: number
  currentBal: number
  credit: number
  autoPay: boolean
  paperStmt: boolean
  eStmt: boolean
}

interface IPatientResponse extends ApiResponse {
  data: IPatient
}

const taskRequest = {
  request: {
    async gettask(facilityKey: string, patientKey: string): Promise<ApiResponse> {
      return secured().get(
        `/Portal/GetTasks/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`
      )
    },

    async getSpecificTask(
      facilityKey: string,
      patientKey: string,
      taskId: number
    ): Promise<
      ApiResponse<{
        taskID: number
        userID: number
        facilityID: number
        patientID: number
        taskTypeID: number
        taskType: string
        info: unknown
      }>
    > {
      return secured().get(
        `/Portal/GetTask/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${taskId}`
      )
    },

    async getCaseHistoryTaskAction(
      facilityKey: string,
      patientKey: string,
      taskId: number
    ): Promise<ApiResponse<CaseHistoryTaskAction>> {
      return secured().get(
        `/Portal/GetCaseHistoryTaskAction/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${taskId}`
      )
    },

    async getFormTaskAction(
      facilityKey: string,
      patientKey: string,
      taskId: number
    ): Promise<ApiResponse<FormTaskAction>> {
      return secured().get(
        `/Portal/GetFormTaskAction/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${taskId}`
      )
    },

    async getFvTaskAction(
      facilityKey: string,
      patientKey: string,
      taskId: number
    ): Promise<ApiResponse<FvTaskAction>> {
      return secured().get(
        `/Portal/GetFvTaskAction/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${taskId}`
      )
    },

    async getStatementTaskAction(
      facilityKey: string,
      patientKey: string,
      taskId: number
    ): Promise<ApiResponse<StatementTaskAction>> {
      return secured().get(
        `/Portal/GetStatementTaskAction/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${taskId}`
      )
    },

    /** GET — returns JSON template string */
    async getPortalConfigData(facilityKey: string, code: string): Promise<ApiResponse<string>> {
      return secured().get(
        `/Portal/GetPortalConfigData/${encodeURIComponent(facilityKey)}/${encodeURIComponent(code)}`
      )
    },

    /**
     * POST /Portal/GetCaseHistoryValue/{facilityKey}/{patientKey}/{caseHistoryId}/{code}
     * Returns ApiValue with optional persisted value string.
     */
    async getCaseHistoryValue(
      facilityKey: string,
      patientKey: string,
      caseHistoryId: number,
      code: string
    ): Promise<ApiResponse<ApiValue>> {
      return secured().post(
        `/Portal/GetCaseHistoryValue/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${caseHistoryId}/${encodeURIComponent(code)}`
      )
    },

    /**
     * POST /Portal/UpdateCaseHistoryValue/{facilityKey}/{patientKey}/{caseHistoryId}/{code}
     * Body: { value: string }
     */
    async updateCaseHistoryValue(
      facilityKey: string,
      patientKey: string,
      caseHistoryId: number,
      code: string,
      value: string
    ): Promise<ApiResponse<ApiBoolResult>> {
      // The value is the patient's answer (PHI): dev builds only, never in production / Sentry.
      if (import.meta.env.DEV) {
        const preview =
          value.length > 400 ? `${value.slice(0, 400)}… (${value.length} chars)` : value
        console.log('[UpdateCaseHistoryValue]', {
          code,
          caseHistoryId,
          valueLength: value.length,
          value: preview
        })
      }
      const res = await secured().post(
        `/Portal/UpdateCaseHistoryValue/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${caseHistoryId}/${encodeURIComponent(code)}`,
        { value }
      )
      const { success, reason } = parseApiBoolResult(res.data)
      if (!success) {
        const msg = (reason && reason.trim()) || 'Unable to save case history value.'
        throw new Error(msg)
      }
      return res
    },

    async patientLookup(payload: unknown): Promise<ApiResponse> {
      return secured().post('/Portal/PatientLookup', payload)
    },

    async getPatient(payload: {
      facilityKey: string
      patientKey: string
    }): Promise<IPatientResponse> {
      return secured().get(
        `/Portal/GetPatient/${encodeURIComponent(payload.facilityKey)}/${encodeURIComponent(payload.patientKey)}`
      )
    },

    async findIncompleteCaseHistory(facilityKey: string, patientKey: string): Promise<ApiResponse> {
      return secured().get(
        `/Portal/FindIncompleteCaseHistory/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`
      )
    },

    async insertCaseHistory(
      facilityKey: string,
      patientKey: string,
      payload: { aa?: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/InsertCaseHistory/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`,
        payload
      )
    },

    /** GET /Portal/GetCaseHistoryProblems/{facilityKey}/{caseHistoryId} */
    async getCaseHistoryProblems(facilityKey: string, caseHistoryId: number): Promise<ApiResponse> {
      return secured().get(
        `/Portal/GetCaseHistoryProblems/${encodeURIComponent(facilityKey)}/${caseHistoryId}`
      )
    },

    /** POST /Portal/InsertCaseHistoryProblem/{facilityKey}/{caseHistoryId}/{ordinal} with body ComplaintRegionName (complaintType, region, name) */
    async insertCaseHistoryProblem(
      facilityKey: string,
      caseHistoryId: number,
      ordinal: number,
      body: { complaintType?: string; region?: string; name?: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/InsertCaseHistoryProblem/${encodeURIComponent(facilityKey)}/${caseHistoryId}/${ordinal}`,
        body
      )
    },

    /** POST /Portal/UpdateCaseHistoryProblem/{facilityKey}/{problemId} with body ComplaintRegionName (complaintType, region, name) */
    async updateCaseHistoryProblem(
      facilityKey: string,
      problemId: number,
      body: { complaintType?: string; region?: string; name?: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryProblem/${encodeURIComponent(facilityKey)}/${problemId}`,
        body
      )
    },

    /** GET /Portal/DeleteCaseHistoryProblem/{facilityKey}/{caseHistoryId}/{problemId} */
    async deleteCaseHistoryProblem(
      facilityKey: string,
      caseHistoryId: number,
      problemId: number
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/DeleteCaseHistoryProblem/${encodeURIComponent(facilityKey)}/${caseHistoryId}/${problemId}`
      )
    },

    /** GET /Portal/GetCaseHistoryProblemValues/{facilityKey}/{caseHistoryProblemId} - returns array of CodeValue (code, value) */
    async getCaseHistoryProblemValues(
      facilityKey: string,
      caseHistoryProblemId: number
    ): Promise<ApiResponse<{ code?: string; value?: string }[]>> {
      return secured().get(
        `/Portal/GetCaseHistoryProblemValues/${encodeURIComponent(facilityKey)}/${caseHistoryProblemId}`
      )
    },

    /** POST /Portal/UpdateCaseHistoryProblemValue/{facilityKey}/{problemId} with body CodeValue (code, value) */
    async updateCaseHistoryProblemValue(
      facilityKey: string,
      problemId: number,
      body: { code?: string; value?: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryProblemValue/${encodeURIComponent(facilityKey)}/${problemId}`,
        body
      )
    },

    /** POST /Portal/UpdateCaseHistoryProblemComplete/{facilityKey}/{caseHistoryProblemId}/{complete} */
    async updateCaseHistoryProblemComplete(
      facilityKey: string,
      caseHistoryProblemId: number,
      complete: boolean
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryProblemComplete/${encodeURIComponent(facilityKey)}/${caseHistoryProblemId}/${complete}`
      )
    },

    /** GET /Portal/GetCaseHistoryProblemOat/{facilityKey}/{caseHistoryProblemId} - returns single CaseHistoryOat */
    async getCaseHistoryProblemOat(
      facilityKey: string,
      caseHistoryProblemId: number
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/GetCaseHistoryProblemOat/${encodeURIComponent(facilityKey)}/${caseHistoryProblemId}`
      )
    },

    /** POST /Portal/UpdateCaseHistoryProblemOatAnswers/{facilityKey}/{caseHistoryProblemId}/{oatId} with body array of OatPatientAnswer (oatID, question, answer) */
    async updateCaseHistoryProblemOatAnswers(
      facilityKey: string,
      caseHistoryProblemId: number,
      oatId: number,
      body: Array<{ oatID?: number; question?: string; answer?: string }>
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryProblemOatAnswers/${encodeURIComponent(facilityKey)}/${caseHistoryProblemId}/${oatId}`,
        body
      )
    },

    /** GET /Portal/GetCaseHistoryConditions/{facilityKey}/{caseHistoryId} */
    async getCaseHistoryConditions(
      facilityKey: string,
      caseHistoryId: number
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/GetCaseHistoryConditions/${encodeURIComponent(facilityKey)}/${caseHistoryId}`
      )
    },

    /** POST /Portal/UpdateCaseHistoryConditions/{facilityKey}/{caseHistoryId} with body array of PortalCondition */
    async updateCaseHistoryConditions(
      facilityKey: string,
      caseHistoryId: number,
      conditions: Array<{
        description: string
        present: boolean
        notPresent: boolean
        past: boolean
      }>
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryConditions/${encodeURIComponent(facilityKey)}/${caseHistoryId}`,
        conditions
      )
    },

    /** GET /Portal/ClearCaseHistoryConditions/{facilityKey}/{caseHistoryId} */
    async clearCaseHistoryConditions(
      facilityKey: string,
      caseHistoryId: number
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/ClearCaseHistoryConditions/${encodeURIComponent(facilityKey)}/${caseHistoryId}`
      )
    },

    async getCaseHistoryFamilyHealthHistory(
      facilityKey: string,
      payload: { caseHistoryId?: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/GetCaseHistoryFamilyHealthHistory/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async deleteCaseHistoryFamilyHealthHistory(
      facilityKey: string,
      payload: { id?: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/DeleteCaseHistoryFamilyHealthHistory/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async updateCaseHistoryFamilyHealthHistory(
      facilityKey: string,
      payload: {
        caseHistoryId?: number
        id?: number
        description?: string
        relation?: string
        present?: boolean
        notPresent?: boolean
        past?: boolean
        unknown?: boolean
      }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryFamilyHealthHistory/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async insertCaseHistoryFamilyHealthHistory(
      facilityKey: string,
      payload: { caseHistoryId?: number; histories?: unknown }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/InsertCaseHistoryFamilyHealthHistory/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async getUsStates(): Promise<ApiResponse> {
      return secured().get('/common/getusstates')
    },

    async updatePatient(
      facilityKey: string,
      patientKey: string,
      payload: {
        lastName?: string
        firstName?: string
        mi?: string
        nickName?: string
        suffix?: string
        address?: string
        address2?: string
        state?: string
        zip?: string
        homePhone?: string
        cellPhone?: string
        email?: string
        dob?: string
        sex?: string
        employStatId?: string
        emerContact?: string
        emerRelation?: string
        emerPhone?: string
      }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdatePatient/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`,
        payload
      )
    },

    async updatePatientField(
      facilityKey: string,
      patientKey: string,
      payload: { field: string; value: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdatePatientField/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`,
        payload
      )
    },

    async updateCaseHistoryAaValue(
      facilityKey: string,
      payload: { caseHistoryId?: string; code?: string; value?: string }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryAaValue/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async updateCaseHistoryAaValues(
      facilityKey: string,
      payload: { caseHistoryId?: string; values?: unknown }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryAaValues/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async getOatAnswerList(payload: unknown): Promise<ApiResponse> {
      return secured().post('/Portal/GetOatAnswerList', payload)
    },

    /** GET /Portal/GetCaseHistoryOats/{facilityKey}/{caseHistoryId} - returns list of OATs, one per complaint. */
    async getCaseHistoryOats(
      facilityKey: string,
      caseHistoryId: number
    ): Promise<ApiResponse<CaseHistoryOat[]>> {
      return secured().get(
        `/Portal/GetCaseHistoryOats/${encodeURIComponent(facilityKey)}/${caseHistoryId}`
      )
    },

    async getCaseHistoryOatQuestions(
      facilityKey: string,
      payload: {
        caseHistoryId?: number
        oadId?: number
        caseHistoryProblemId?: number
      }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/GetCaseHistoryOatQuestions/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async updateCaseHistoryProblemOats(
      facilityKey: string,
      payload: { caseHistoryProblemId?: number; questionList?: unknown }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryProblemOats/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async getEthnicityList(facilityId: number): Promise<ApiResponse> {
      return secured().get(`/Portal/getethnicitylist/${facilityId}`)
    },

    async getRaceList(facilityId: number): Promise<ApiResponse> {
      return secured().get(`/Portal/getracelist/${facilityId}`)
    },

    async getLanguageList(facilityId: number): Promise<ApiResponse> {
      return secured().get(`/Portal/getlanguagelist/${facilityId}`)
    },

    async updateCaseHistoryDemographic(
      facilityKey: string,
      payload: {
        caseHistoryId?: number
        ethnicity?: string
        races?: string
        languages?: string
        declineEthnicity?: string
        declineRace?: string
        declineLanguage?: string
      }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryDemographic/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async updateCaseHistoryQuestions(
      facilityKey: string,
      payload: {
        caseHistoryId?: number
        questions?: Array<{ description: string; answer: string }>
      }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateCaseHistoryQuestions/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async getCaseHistoryQuestions(
      facilityKey: string,
      payload: { caseHistoryId?: number }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/GetCaseHistoryQuestions/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async getCaseHistoryDemographic(
      facilityKey: string,
      payload: { caseHistoryId?: number }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/GetCaseHistoryDemographic/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async updateCaseHistoryComplete(
      facilityKey: string,
      patientKey: string,
      caseHistoryId: number
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/UpdateCaseHistoryComplete/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${caseHistoryId}`
      )
    },

    async getCaseHistoryAaValues(
      facilityKey: string,
      payload: { caseHistoryId?: number }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/GetCaseHistoryAaValues/${encodeURIComponent(facilityKey)}`,
        payload
      )
    },

    async updateTaskComplete(
      facilityKey: string,
      patientKey: string,
      id: number
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/UpdateTaskComplete/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${id}`
      )
    },

    async getFacilityFormJson(facilityformid: number): Promise<ApiResponse> {
      return secured().get(`/form/facilityformjson/${facilityformid}`)
    },

    async getFacilityFormJsonQueryObj(payload: {
      facilityformid: number
      patientid: number
    }): Promise<ITaskJSONFormQueryData> {
      return (await secured().post('/form/FacilityFormJson', payload))
        .data as ITaskJSONFormQueryData
    },

    async insertPatientForm(payload: {
      facilityformid: number
      patientid: number
      data: string
    }): Promise<ApiResponse> {
      return secured().post('/form/PatientForm', payload)
    },

    async uploadPatientForm(payload: unknown): Promise<ApiResponse> {
      return secured().post('/Portal/UploadPatientForm', payload)
    },

    async insertInsuranceInfo(
      facilityKey: string,
      patientKey: string,
      payload: {
        relation?: string
        firstname?: string
        mi?: string
        dob?: string
      }
    ): Promise<ApiResponse> {
      return secured().post(
        `/Portal/InsertInsuranceInfo/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`,
        payload
      )
    },

    async virusScan(payload: {
      facilityid: number
      patientId: string
      dataUri: string
    }): Promise<ApiResponse> {
      return secured().post('/clam/VirusScan', payload)
    },

    async updateCaseHistory(
      facilityKey: string,
      payload: { id?: number; aa?: boolean; wc?: boolean }
    ): Promise<ApiResponse> {
      return secured().post(`/Portal/UpdateCaseHistory/${encodeURIComponent(facilityKey)}`, payload)
    },

    async deletePatientForm(id: number): Promise<ApiResponse> {
      return secured().get(`/form/DeletePatientForm/${id}`)
    },

    /** GET /Portal/GetCaseHistoryPortalConfigs/{facilityKey}/{patientKey}/{caseHistoryId} */
    async getCaseHistoryPortalConfigs(
      facilityKey: string,
      patientKey: string,
      caseHistoryId: number
    ): Promise<ApiResponse<CaseHistoryPortalConfig[]>> {
      return secured().get(
        `/Portal/GetCaseHistoryPortalConfigs/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${caseHistoryId}`
      )
    },

    /** GET /Portal/GetCaseHistoryPortalConfig/{facilityKey}/{patientKey}/{caseHistoryId}/{portalConfigCode} */
    async getCaseHistoryPortalConfig(
      facilityKey: string,
      patientKey: string,
      caseHistoryId: number,
      portalConfigCode: string
    ): Promise<ApiResponse<CaseHistoryPortalConfig>> {
      return secured().get(
        `/Portal/GetCaseHistoryPortalConfig/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${caseHistoryId}/${encodeURIComponent(portalConfigCode)}`
      )
    },

    /** GET /Portal/InsertCaseHistoryPortalConfig/{facilityKey}/{patientKey}/{caseHistoryId}/{portalConfigCode} */
    async insertCaseHistoryPortalConfig(
      facilityKey: string,
      patientKey: string,
      caseHistoryId: number,
      portalConfigCode: string
    ): Promise<ApiResponse<ApiIdResult>> {
      return secured().get(
        `/Portal/InsertCaseHistoryPortalConfig/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${caseHistoryId}/${encodeURIComponent(portalConfigCode)}`
      )
    },

    /**
     * POST /Portal/UpdateCaseHistoryPortalConfigData/{facilityKey}/{patientKey}/{portalConfigId}/{code}
     * portalConfigId = CaseHistoryPortalConfig.id returned by InsertCaseHistoryPortalConfig / GetCaseHistoryPortalConfig
     * code = "Data" for portal config template; code = "Answers" for patient questionnaire answers
     * Body: { value: string }
     */
    async updateCaseHistoryPortalConfigData(
      facilityKey: string,
      patientKey: string,
      portalConfigId: number,
      code: string,
      value: string
    ): Promise<ApiResponse<ApiBoolResult>> {
      return secured().post(
        `/Portal/UpdateCaseHistoryPortalConfigData/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}/${portalConfigId}/${encodeURIComponent(code)}`,
        { value }
      )
    }
  }
}

export default taskRequest
